import * as React from 'react';

export interface UseDropZoneOptions {
  onFilesSelected?: (files: FileList) => void;
  accept?: string;
  multiple?: boolean;
  disabled?: boolean;
  containerRef?: React.RefObject<HTMLElement | null>;
}

export interface UseDropZoneReturn {
  isDragging: boolean;
  dropZoneProps: {
    onDragEnter: (e: React.DragEvent<HTMLElement>) => void;
    onDragOver: (e: React.DragEvent<HTMLElement>) => void;
    onDragLeave: (e: React.DragEvent<HTMLElement>) => void;
    onDrop: (e: React.DragEvent<HTMLElement>) => void;
  };
  openFilePicker: () => void;
}

/** Check whether a file matches a single accept token (exact MIME, wildcard MIME, or extension). */
export function matchesAcceptToken(
  file: Pick<File, 'name' | 'type'>,
  token: string,
): boolean {
  const trimmed = token.trim();
  if (trimmed.startsWith('.')) {
    // Extension match: ".pdf"
    return file.name.toLowerCase().endsWith(trimmed.toLowerCase());
  }
  if (trimmed.includes('/*')) {
    // Wildcard MIME: "image/*"
    const prefix = trimmed.split('/')[0];
    return file.type.startsWith(`${prefix}/`);
  }
  // Exact MIME: "image/png"
  return file.type === trimmed;
}

/** Filter files against a comma-separated accept string. Returns matching files. */
function filterFiles(files: File[], accept: string): File[] {
  const tokens = accept.split(',');
  return files.filter((file) =>
    tokens.some((token) => matchesAcceptToken(file, token)),
  );
}

/** Build a FileList from an array of Files using DataTransfer. */
export function toFileList(files: File[]): FileList {
  const dt = new DataTransfer();
  for (const file of files) {
    dt.items.add(file);
  }
  return dt.files;
}

export function useDropZone({
  onFilesSelected,
  accept,
  multiple = true,
  disabled = false,
  containerRef,
}: UseDropZoneOptions): UseDropZoneReturn {
  const [isDragging, setIsDragging] = React.useState(false);
  const dragCounterRef = React.useRef(0);
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);

  const isActive = !disabled && onFilesSelected !== undefined;

  const handleDragEnter = React.useCallback(
    (e: React.DragEvent<HTMLElement>) => {
      e.preventDefault();
      e.stopPropagation();
      if (!isActive) return;
      if (!e.dataTransfer.types.includes('Files')) return;
      dragCounterRef.current += 1;
      if (dragCounterRef.current === 1) {
        setIsDragging(true);
      }
    },
    [isActive],
  );

  const handleDragOver = React.useCallback(
    (e: React.DragEvent<HTMLElement>) => {
      e.preventDefault();
      e.stopPropagation();
    },
    [],
  );

  const handleDragLeave = React.useCallback(
    (e: React.DragEvent<HTMLElement>) => {
      e.preventDefault();
      e.stopPropagation();
      if (!isActive) return;
      dragCounterRef.current -= 1;
      if (dragCounterRef.current <= 0) {
        dragCounterRef.current = 0;
        setIsDragging(false);
      }
    },
    [isActive],
  );

  const handleDrop = React.useCallback(
    (e: React.DragEvent<HTMLElement>) => {
      e.preventDefault();
      e.stopPropagation();
      dragCounterRef.current = 0;
      setIsDragging(false);
      if (!isActive) return;

      let files = Array.from(e.dataTransfer.files);

      // Accept filtering
      if (accept) {
        files = filterFiles(files, accept);
      }

      // multiple enforcement
      if (!multiple && files.length > 1) {
        files = [files[0]];
      }

      if (files.length === 0) return;

      onFilesSelected?.(toFileList(files));
    },
    [isActive, accept, multiple, onFilesSelected],
  );

  // Document-level drop prevention (safety net)
  React.useEffect(() => {
    const doc = containerRef?.current?.ownerDocument ?? document;

    const handleDocDrop = (e: Event) => {
      e.preventDefault();
      if (dragCounterRef.current > 0) {
        dragCounterRef.current = 0;
        setIsDragging(false);
      }
    };

    doc.addEventListener('drop', handleDocDrop);
    return () => {
      doc.removeEventListener('drop', handleDocDrop);
      fileInputRef.current?.remove();
      fileInputRef.current = null;
    };
  }, [containerRef]);

  const openFilePicker = React.useCallback(() => {
    if (!isActive) return;

    const doc = containerRef?.current?.ownerDocument ?? document;
    fileInputRef.current?.remove();

    const input = doc.createElement('input');
    input.type = 'file';
    input.hidden = true;
    if (accept) {
      input.accept = accept;
    }
    input.multiple = multiple;

    const removeInput = () => {
      input.remove();
      if (fileInputRef.current === input) {
        fileInputRef.current = null;
      }
    };

    input.onchange = () => {
      const files = input.files;
      removeInput();
      if (files && files.length > 0) {
        onFilesSelected?.(files);
      }
    };
    input.oncancel = removeInput;

    fileInputRef.current = input;
    doc.body.appendChild(input);
    input.click();
  }, [isActive, accept, multiple, onFilesSelected, containerRef]);

  return {
    isDragging,
    dropZoneProps: {
      onDragEnter: handleDragEnter,
      onDragOver: handleDragOver,
      onDragLeave: handleDragLeave,
      onDrop: handleDrop,
    },
    openFilePicker,
  };
}
